import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query'
import {useServices} from '@/services/context'
import {useToast} from '@/hooks/use-toast'
import {useI18n} from '@/i18n'
import {ApiError} from '@/api/client'

const SAP_HCM_KEY = 'inbound-connectors-sap-hcm-config'
const SAP_SF_EC_KEY = 'inbound-connectors-sap-sf-ec-config'
const SAP_ABAP_KEY = 'outbound-connectors-sap-abap-config'

// WHY: SAP config rows are created lazily via POST when the user clicks
// "Create configuration". A 404 GET is the expected "config not yet created"
// state — map it to null so the empty-state branch fires without surfacing
// an error or polluting the frontend-error log.
async function nullOn404<T>(promise: Promise<T>): Promise<T | null> {
    try {
        return await promise
    } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
            return null
        }
        throw err
    }
}

export function useSapHcmConfig(id: string | undefined) {
    const services = useServices()
    return useQuery({
        queryKey: [SAP_HCM_KEY, id],
        queryFn: () => nullOn404(services.inboundConnectors.sapHcmConfig(id!)),
        enabled: !!id,
        retry: false,
    })
}

export function useCreateSapHcmConfig(id: string) {
    const services = useServices()
    const queryClient = useQueryClient()
    const {toast} = useToast()
    const i18n = useI18n()
    return useMutation({
        mutationFn: (data: unknown) => services.inboundConnectors.createSapHcmConfig(id, data),
        onSuccess: () => {
            toast({description: i18n.inboundConnectors.sapHcm.saveSuccess})
            queryClient.invalidateQueries({queryKey: [SAP_HCM_KEY, id]})
        },
    })
}

export function useUpdateSapHcmConfig(id: string) {
    const services = useServices()
    const queryClient = useQueryClient()
    const {toast} = useToast()
    const i18n = useI18n()
    return useMutation({
        mutationFn: (data: unknown) => services.inboundConnectors.updateSapHcmConfig(id, data),
        onSuccess: () => {
            toast({description: i18n.inboundConnectors.sapHcm.saveSuccess})
            queryClient.invalidateQueries({queryKey: [SAP_HCM_KEY, id]})
        },
    })
}

export function useSapSfEcConfig(id: string | undefined) {
    const services = useServices()
    return useQuery({
        queryKey: [SAP_SF_EC_KEY, id],
        queryFn: () => nullOn404(services.inboundConnectors.sapSfEcConfig(id!)),
        enabled: !!id,
        retry: false,
    })
}

export function useCreateSapSfEcConfig(id: string) {
    const services = useServices()
    const queryClient = useQueryClient()
    const {toast} = useToast()
    const i18n = useI18n()
    return useMutation({
        mutationFn: (data: unknown) => services.inboundConnectors.createSapSfEcConfig(id, data),
        onSuccess: () => {
            toast({description: i18n.inboundConnectors.sapSfEc.saveSuccess})
            queryClient.invalidateQueries({queryKey: [SAP_SF_EC_KEY, id]})
        },
    })
}

export function useUpdateSapSfEcConfig(id: string) {
    const services = useServices()
    const queryClient = useQueryClient()
    const {toast} = useToast()
    const i18n = useI18n()
    return useMutation({
        mutationFn: (data: unknown) => services.inboundConnectors.updateSapSfEcConfig(id, data),
        onSuccess: () => {
            toast({description: i18n.inboundConnectors.sapSfEc.saveSuccess})
            queryClient.invalidateQueries({queryKey: [SAP_SF_EC_KEY, id]})
        },
    })
}

export function useSapAbapConfig(id: string | undefined) {
    const services = useServices()
    return useQuery({
        queryKey: [SAP_ABAP_KEY, id],
        queryFn: () => nullOn404(services.outboundConnectors.sapAbapConfig(id!)),
        enabled: !!id,
        retry: false,
    })
}

export function useCreateSapAbapConfig(id: string) {
    const services = useServices()
    const queryClient = useQueryClient()
    const {toast} = useToast()
    const i18n = useI18n()
    return useMutation({
        mutationFn: (data: unknown) => services.outboundConnectors.createSapAbapConfig(id, data),
        onSuccess: () => {
            toast({description: i18n.connectors.sapAbap.saveSuccess})
            queryClient.invalidateQueries({queryKey: [SAP_ABAP_KEY, id]})
        },
    })
}

export function useUpdateSapAbapConfig(id: string) {
    const services = useServices()
    const queryClient = useQueryClient()
    const {toast} = useToast()
    const i18n = useI18n()
    return useMutation({
        mutationFn: (data: unknown) => services.outboundConnectors.updateSapAbapConfig(id, data),
        onSuccess: () => {
            toast({description: i18n.connectors.sapAbap.saveSuccess})
            queryClient.invalidateQueries({queryKey: [SAP_ABAP_KEY, id]})
        },
    })
}
